import { DataStore } from '../stores';
import { FILTER_SEARCH_KEYS } from '../constants';
import { action, computed, observable, toJS, isArrayLike } from 'mobx';
import get from 'lodash/get';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { FilterSearchKeys, FilterProps } from '../models/components/Filter';
import { ClearIndividualFilters } from '../models/configuration/filter/ClearIndividualFilters';
import { getNonFilterParams } from '../utils';
import { isGUID } from '../utils/isGUID';
import { QueryStringOptions } from '../models/configuration/explorer/QueryStringOptions';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import {
    isNullOrEmpty,
    isNullOrUndefined,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';

export class FilterStore extends BaseStore {
    dataStore: DataStore;
    @observable filtersOpen: boolean = false;
    @observable showShareableLinkConfirm: boolean = false;

    constructor(stores: Stores) {
        super(stores);
        this.dataStore = stores.data;
    }

    shouldRenderFilter(filter): boolean {
        const { noDataDisplay = 'show' } = this.dataStore.config.filterOptions;
        const { values } = filter;
        return (
            noDataDisplay === 'show' ||
            (noDataDisplay === 'hide' && !isNullOrEmpty(toJS(values)))
        );
    }

    @computed
    get filters() {
        const filters = [];
        for (const filter of this.dataStore.config.filtersConfig) {
            const isDocumentFilter = this.dataStore.config.isDocumentFilter(
                filter.property
            );
            const fundTypeEntity = this.dataStore.config.entityType;
            if (isDocumentFilter || filter.type === fundTypeEntity) {
                const orchestratedFilter = {
                    ...filter,
                    values: get(this.dataStore.filters, filter.property, []),
                    filterSearchKey: get(FILTER_SEARCH_KEYS, filter.type, ''),
                };
                if (this.shouldRenderFilter(orchestratedFilter)) {
                    filters.push(orchestratedFilter);
                }
            } else {
                console.warn(
                    `The configured filter type (${filter.type}) does not match the Fund entity type (${fundTypeEntity})`
                );
            }
        }

        return filters;
    }

    @computed
    get filtersByProperty() {
        return this.filters.reduce((config, filter) => {
            if (!config[filter.property]) {
                config[filter.property] = filter;
            }
            return config;
        }, {});
    }

    @computed
    get minimumFiltersNeeded(): Array<string> | undefined {
        const minimumFiltersNeeded =
            this.dataStore &&
            this.dataStore.config &&
            this.dataStore.config.filterOptions &&
            this.dataStore.config.filterOptions.minimumFiltersNeeded;
        return minimumFiltersNeeded;
    }

    isFilterRequired = (property: string) => {
        return (
            isArrayLike(this.minimumFiltersNeeded) &&
            this.minimumFiltersNeeded.includes(property)
        );
    };

    @action
    toggleFilters = () => {
        this.filtersOpen = !this.filtersOpen;
    };

    @computed
    get filtersCount(): number {
        const uniqueFilters = [];
        const filtersConfigByProperty = this.stores.data.config
            .filtersConfigByProperty;
        return [
            ...this.dataStore.filterSearch,
            ...this.dataStore.documentFilterSearch,
            ...this.dataStore.documentPropertySearch,
        ]
            .filter(
                filter => filtersConfigByProperty[filter.property] !== undefined
            )
            .reduce((count, filter) => {
                if (
                    uniqueFilters.find(
                        property => filter.property === property
                    ) === undefined
                ) {
                    uniqueFilters.push(filter.property);
                    count++;
                }
                return count;
            }, 0);
    }

    @computed
    get filtersActive(): boolean {
        return this.filtersCount > 0 ? true : false;
    }

    @computed
    get showShareableLink(): boolean {
        return (
            this.dataStore.config.shareableLink &&
            this.dataStore.config.shareableLink.show
        );
    }

    @computed
    get shareableLinkLabel(): string {
        return (
            (this.dataStore.config.shareableLink &&
                this.dataStore.config.shareableLink.label) ||
            'Share'
        );
    }

    @computed
    get shareableLinkConfirmation(): string {
        return (
            (this.dataStore.config.shareableLink &&
                this.dataStore.config.shareableLink.confirmation) ||
            'Successfully copied to clipboard'
        );
    }

    getFilterParamsForQueryString = (
        options: QueryStringOptions = {}
    ): { [key: string]: any } => {
        const params = getNonFilterParams(window.location.search);
        const {
            inputKeys = [],
            aliases = {},
            dataStoreQueries = [],
            queries = [],
            sort = false,
        } = options;
        if (inputKeys && inputKeys.length > 0) {
            const { appDataProps } = this.dataStore;
            for (const inputKey of inputKeys) {
                const value = appDataProps[inputKey];
                if (typeof value !== 'undefined') {
                    params[inputKey] = value;
                }
            }
        }

        this.dataStore.loadDataStoreQueries(params, dataStoreQueries);
        this.dataStore.loadQueries(params, queries);

        this.filters.forEach(filter => {
            const values = filter.values
                .filter(value =>
                    this.isChecked(
                        filter.property,
                        value.value,
                        filter.filterSearchKey
                    )
                )
                .map(v => encodeURIComponent(v.value));
            if (values.length > 0) {
                const filterValues = sort ? [...values].sort() : values;
                params[`_${filter.property}`] = filterValues.join('|');
            }
        });

        if (aliases.key && aliases.key.length > 0) {
            for (const keyAlias of aliases.key) {
                const { replacement, target } = keyAlias;
                params[replacement] = params[target];
                delete params[target];
            }
        }
        if (aliases.value && aliases.value.length > 0) {
            const valueAliasMap = new Map<any, any>();
            for (const valueAlias of aliases.value) {
                const { replacement, target } = valueAlias;
                valueAliasMap.set(target, replacement);
            }
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (valueAliasMap.has(value)) {
                    params[key] = valueAliasMap.get(value);
                }
            });
        }
        return params;
    };

    getSelectedViewParamsForQueryString = (params: {
        [key: string]: string;
    }) => {
        const { config } = this.dataStore;
        const { selectedFundType, selectedModule, columnConfig } = config;

        if (selectedFundType && selectedFundType.fundType) {
            Object.assign(params, {
                categoryTab: encodeURIComponent(
                    selectedFundType.fundType.toLowerCase()
                ),
            });
        } else {
            console.warn(
                'Could not determine selected Fund Type for shareable link'
            );
        }
        if (selectedModule && selectedModule.id) {
            Object.assign(params, {
                tab: encodeURIComponent(selectedModule.id.toLowerCase()),
            });
        } else {
            console.warn(
                'Could not determine selected Module for shareable link'
            );
        }

        const groupColumn = {};
        columnConfig.forEach((column, idx) => {
            let groupColumnKey;
            if (column.type === 'dropdown') {
                // Due to the guid's being generated on load, the previous guid will not match to a new instance
                if (column.key && !isGUID(column.key)) {
                    groupColumnKey = `columnGroup_${encodeURIComponent(
                        column.key
                    )}`;
                    groupColumn[groupColumnKey] = this.getSelectedColumnValue(
                        column,
                        idx
                    );
                } else {
                    groupColumnKey = `columnGroup_${idx}`;
                    groupColumn[groupColumnKey] = this.getSelectedColumnValue(
                        column,
                        idx
                    );
                }
                Object.assign(params, groupColumn);
            }
        });
    };

    getSelectedColumnValue(column: ColumnConfig, idx: number) {
        const { columnStore } = this.dataStore;
        const { getSelectedGroup, selectedGroupMap } = columnStore;
        const selectedColumnValue = getSelectedGroup(column.key);
        if (selectedColumnValue.key) {
            return encodeURIComponent(selectedColumnValue.key);
        } else {
            return selectedGroupMap.get(idx);
        }
    }

    @computed
    get shareableLinkHref(): string {
        const { origin, pathname } = window.location;
        const params = this.getFilterParamsForQueryString();
        this.getSelectedViewParamsForQueryString(params);
        const searchTerms = Object.keys(params).map(key => {
            return `${key}=${params[key]}`;
        });
        const search = searchTerms.join('&');
        return `${origin}${pathname}?${search}`;
    }

    @action
    copyShareableLinkToClipboard = () => {
        const element = document.createElement('textarea');
        element.value = this.shareableLinkHref;
        element.setAttribute('readonly', '');
        element.style.position = 'absolute';
        element.style.left = '-9999px';
        document.body.appendChild(element);
        element.select();
        document.execCommand('copy');
        document.body.removeChild(element);
        this.showShareableLinkConfirm = true;
    };

    @action
    closeShareableLinkConfirm = () => {
        this.showShareableLinkConfirm = false;
    };

    @computed
    get clearFiltersLabel(): string {
        let label = this.dataStore.config.clearFiltersLabel;
        if (isNullOrUndefined(label)) {
            label = 'Clear filters';
        }
        return label;
    }

    @computed
    get clearIndividualFilters(): ClearIndividualFilters {
        return (
            this.dataStore.config.clearIndividualFilters || {
                enabled: false,
                align: 'left',
            }
        );
    }

    @action
    clearFilters = () => this.dataStore.clearFilters();

    @action
    clearFiltersOfType = (
        property: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const filterConfig = this.filtersByProperty[property];
        this.dataStore.clearFiltersOfType(property, filterSearchKey);
        if (filterConfig.id) {
            this.removeFiltersByParentId(filterConfig.id, filterSearchKey);
        }
    };

    @action
    toggleFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        checked: boolean,
        filter: FilterProps
    ) => {
        if (filter.ui === 'dropdown') {
            this.toggleDropdownFilter(property, value, filterSearchKey, filter);
        } else {
            if (checked) {
                this.removeFilter(property, value, filterSearchKey);
            } else {
                this.addFilter(property, value, filterSearchKey);
            }
        }
        if (filter.id) {
            this.removeFiltersByParentId(filter.id, filterSearchKey);
        }
    };

    @action
    toggleDropdownFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        filter: FilterProps
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        let filterAlreadySet = false;
        // remove existing filter
        if (updatedSearch && updatedSearch.length > 0) {
            for (const oldRecord of updatedSearch) {
                const { values: oldValues } = oldRecord;
                for (const oldValue of oldValues) {
                    if (oldValue !== value) {
                        this.removeFilter(property, oldValue, filterSearchKey);
                    } else {
                        filterAlreadySet = true;
                    }
                }
            }
        }
        // add new filter
        if (!filterAlreadySet) {
            this.addFilter(property, value, filterSearchKey);
        }
    };

    @action
    addFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        const existingPropertyIndex = updatedSearch.findIndex(
            search => search.property === property
        );

        if (existingPropertyIndex >= 0) {
            updatedSearch[existingPropertyIndex].values.push(value);
            this.dataStore[filterSearchKey] = updatedSearch;
        } else {
            updatedSearch.push({
                property,
                values: [value],
                matchtype: 'MATCH',
            });
            this.dataStore[filterSearchKey] = updatedSearch;
        }
    };

    @action
    removeFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        const existingPropertyIndex = updatedSearch.findIndex(
            search => search.property === property
        );

        if (existingPropertyIndex >= 0) {
            updatedSearch[existingPropertyIndex].values = updatedSearch[
                existingPropertyIndex
            ].values.filter(existingValue => existingValue !== value);

            if (updatedSearch[existingPropertyIndex].values.length > 0) {
                this.dataStore[filterSearchKey] = updatedSearch;
            } else {
                this.dataStore[filterSearchKey] = updatedSearch.filter(
                    search => search.property !== property
                );
            }
        } else {
            this.dataStore[filterSearchKey] = updatedSearch.filter(
                search => search.property !== property
            );
        }
    };

    @action
    removeFiltersByParentId = (
        parentId: string,
        filterSearchKey: FilterSearchKeys
    ): void => {
        // clear direct children of parent id
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        this.dataStore[filterSearchKey] = updatedSearch.filter(filter => {
            const filterConfig = this.filtersByProperty[filter.property];
            return !filterConfig.parentId || filterConfig.parentId !== parentId;
        });
        // apply recursive logic to clear children of children
        const childFilters = updatedSearch.filter(filter => {
            const filterConfig = this.filtersByProperty[filter.property];
            return filterConfig.parentId === parentId;
        });
        for (const filter of childFilters) {
            const filterConfig = this.filtersByProperty[filter.property];
            if (filterConfig.id) {
                this.removeFiltersByParentId(filterConfig.id, filterSearchKey);
            }
        }
    };

    filterValue = (property: string, filterSearchKey: FilterSearchKeys) => {
        const existingProperty = this.dataStore[filterSearchKey].find(
            search => search.property === property
        );

        if (existingProperty) {
            return (
                existingProperty.values &&
                existingProperty.values.length > 0 &&
                existingProperty.values[0]
            );
        }
        return '';
    };

    isChecked = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ): boolean => {
        const existingProperty = this.dataStore[filterSearchKey].find(
            search => search.property === property
        );

        if (existingProperty) {
            return existingProperty.values.includes(value);
        }

        return false;
    };

    isActive = (property: string, value: string): boolean => {
        const filterKeys = Object.keys(this.dataStore.activeFilters);
        if (filterKeys.includes(property)) {
            return this.dataStore.activeFilters[property]
                .map(filterValue => filterValue.value)
                .includes(value);
        }

        return true;
    };
}



// WEBPACK FOOTER //
// ./src/stores/FilterStore.ts